/**
 * Vine (2026-10-05, Matt — painted, not placed). A vine is PAINTED onto a host: the
 * element stores only the painted coverage — `dabs`, circles in the host's own surface
 * coordinates (walls: station along the path × height above the wall base), so the vine
 * follows when its wall moves or changes height — and which face (`sideFlip`, the
 * fixture convention: false = the interior face). The stems and leaves are GROWN from
 * that coverage by a seeded growth routine at generation time (bim-geometry
 * categories/vine), never stored. `hostId` carries the host, so deleting the wall
 * deletes its vines.
 *
 * The TYPE is the species (vineSpecies.ts); the instance carries the character sliders.
 * Walls first; columns and beams (trellises) are the next hosts.
 */
import type { CategorySchema } from '../../registry'
import { VINE_SPECIES } from './vineSpecies'

export const vineSchema: CategorySchema = {
  id: 'vine',
  label: 'Vine',
  pickRank: 2,
  typeParams: {
    groups: [
      {
        label: 'Vine',
        params: [
          {
            key: 'vineSpecies', label: 'Species', kind: 'choice', default: 'creepingFig',
            choices: VINE_SPECIES.map((s) => ({ value: s.id, label: `${s.name} (${s.botanical})` })),
          },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Growth',
        params: [
          // 0 = a young planting just climbing; 1 = full, overgrown coverage
          { key: 'maturity', label: 'Maturity', kind: 'number', default: 0.75, min: 0.05, max: 1 },
          { key: 'leafDensity', label: 'Leaf Density', kind: 'number', default: 1, min: 0.2, max: 2 },
          // how far growth wanders past the painted edge (0 = stays inside it)
          { key: 'spill', label: 'Spill Past Edge', kind: 'number', default: 0.3, min: 0, max: 1 },
          // a different natural pattern over the same coverage
          { key: 'seed', label: 'Pattern', kind: 'number', default: 1, min: 0, max: 9999 },
        ],
      },
      {
        label: 'Painted Area',
        params: [
          // positional: the coverage belongs to THIS host face — eyedropper never copies it
          { key: 'dabs', label: 'Coverage', kind: 'data', default: null, positional: true, hidden: true },
          { key: 'sideFlip', label: 'Other Face', kind: 'bool', default: false, positional: true, hidden: true },
        ],
      },
    ],
  },
}
